import { useRemoteResource } from "./useRemoteResource";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { compositeNamesByRosterName } from "../utils/compositeJudges";

// #642 — the join tables behind the Order of Go.
//
// A score-ride row carries riderId / horseId / testId and NOTHING else: reconcile
// never denormalizes a name onto it. So every string the announcer reads out is
// resolved from these.
//
// people + horses have no per-show filter on their routes (same as scribe-fe's
// hydrate()) — they're org-wide.
//
// #2190 — no local cache: a network failure leaves whatever roster is already
// loaded exactly as it is (see useRemoteResource).

export function usePeople() {
  const api = useScoreApi();
  return useRemoteResource(async () => unwrap(await api.people.listPeople()) ?? [], [api]);
}

export function useHorses() {
  const api = useScoreApi();
  return useRemoteResource(async () => unwrap(await api.horses.listHorses()) ?? [], [api]);
}

// Test is a per-RIDE property (#435 — class.testId is only a hint), and
// there's no bulk test route, so fetch exactly the tests this ring's rides
// reference.
export function useTestsForRides(testIds) {
  const api = useScoreApi();
  const idsKey = [...new Set((testIds || []).filter(Boolean))].sort().join(",");

  return useRemoteResource(async () => {
    if (!idsKey) return [];
    const wanted = idsKey.split(",");
    const fetched = await Promise.all(
      wanted.map((id) =>
        api.tests
          .getTest(id)
          .then(unwrap)
          .catch(() => null),
      ),
    );
    return fetched.filter(Boolean);
  }, [api, idsKey]);
}

// #3419 -- composite judges for a show: the roster plus each mapping, folded into
// Map<composite roster name, real judge names (primary first)>. Revalidates on the
// same entity tick as the class list, so a mapping edited mid-show is picked up.
// A failed fetch leaves the last map in place (useRemoteResource) and a show with no
// composite judge is an empty map, so an ordinary class is never touched.
export function useCompositeJudgeNames(showId, nonce = 0) {
  const api = useScoreApi();
  return useRemoteResource(
    async () => {
      if (!showId) return new Map();
      const [judges, maps] = await Promise.all([
        api.judges.listJudges(showId).then(unwrap),
        api.judges.listCompositeJudgeMaps(showId).then(unwrap),
      ]);
      return compositeNamesByRosterName(judges ?? [], maps ?? []);
    },
    [api, showId, nonce],
  );
}
